import { ref } from "vue"
import api from "./useApi"
import { url } from "../helper"
import { toast } from "vue-sonner"
import { useI18n } from "vue-i18n"

interface PlaybackSessionOptions {
	deviceId: string
	contentId: number | string
	onMaxStreamsReached?: () => void
}

interface StartPlaybackResponse {
	success: boolean
	session_id: string
	message: string
}

interface ErrorResponse {
	error: string
	max_streams?: number
}

export function usePlaybackSession() {
	const { t } = useI18n()
	const sessionId = ref<string | null>(null)
	const heartbeatInterval = ref<number | null>(null)
	const isActive = ref(false)
	const isEnding = ref(false) // Prevent race conditions

	/**
	 * Start a playback session
	 */
	const startSession = async (options: PlaybackSessionOptions): Promise<boolean> => {
		try {
			const response = await api.post<StartPlaybackResponse>(`${url}/api/playback/start`, {
				device_id: options.deviceId,
				content_id: options.contentId,
			})

			if (response.data.success) {
				sessionId.value = response.data.session_id
				isActive.value = true
				return true
			}

			return false
		} catch (error: any) {
			// Check if it's a max streams error
			if (error.response?.status === 400 || error.response?.status === 429) {
				const errorData = error.response?.data as ErrorResponse

				if (errorData.error?.includes("Maximum concurrent streams")) {
					// Show toast notification
					toast.error(t("playback.maxStreamsReached", { max: errorData.max_streams || 3 }), {
						duration: 5000,
					})

					// Call optional callback
					if (options.onMaxStreamsReached) {
						options.onMaxStreamsReached()
					}
				} else {
					toast.error(errorData.error || t("playback.errorStarting"))
				}
			} else {
				console.error("Failed to start playback session:", error)
				toast.error(t("playback.errorStarting"))
			}

			return false
		}
	}

	/**
	 * Send heartbeat to keep session alive
	 */
	const sendHeartbeat = async (position: number): Promise<void> => {
		if (!sessionId.value || !isActive.value) {
			return
		}

		try {
			await api.post(`${url}/api/playback/heartbeat`, {
				session_id: sessionId.value,
				position: Math.floor(position),
			})
		} catch (error) {
			console.error("Failed to send heartbeat:", error)
			// Don't show toast for heartbeat errors to avoid spam
		}
	}

	/**
	 * Start automatic heartbeat (every 15 seconds)
	 */
	const startHeartbeat = (getPosition: () => number): void => {
		// Stop existing heartbeat first to avoid duplicates
		stopHeartbeat()

		heartbeatInterval.value = window.setInterval(() => {
			const position = getPosition()
			sendHeartbeat(position)
		}, 15000) // 15 seconds
	}

	/**
	 * Stop automatic heartbeat
	 */
	const stopHeartbeat = (): void => {
		if (heartbeatInterval.value) {
			clearInterval(heartbeatInterval.value)
			heartbeatInterval.value = null
		}
	}

	/**
	 * Pause automatic heartbeat (alias for stopHeartbeat)
	 */
	const pauseHeartbeat = (): void => {
		stopHeartbeat()
	}

	/**
	 * End playback session
	 * @param useBeacon - If true, use sendBeacon for reliable request during page unload
	 */
	const endSession = async (useBeacon: boolean = false): Promise<void> => {
		if (!sessionId.value || !isActive.value || isEnding.value) {
			return
		}

		// Set flag immediately to prevent duplicate calls
		isEnding.value = true

		stopHeartbeat()

		try {
			if (useBeacon && navigator.sendBeacon) {
				// Use sendBeacon for more reliable delivery during page unload
				// Note: sendBeacon only supports POST, so we keep using the POST endpoint
				const data = JSON.stringify({
					session_id: sessionId.value,
				})

				const blob = new Blob([data], { type: "application/json" })
				const beaconUrl = `${url}/api/playback/end`

				// Get auth token from localStorage
				const token = localStorage.getItem("access_token")
				const headers = {
					type: "application/json",
					Authorization: `Bearer ${token}`,
				}

				// Note: sendBeacon doesn't support custom headers in all browsers
				// So we'll try both methods
				const sent = navigator.sendBeacon(beaconUrl, blob)

				if (!sent) {
					// Fallback to DELETE request if sendBeacon fails
					await api.delete(`${url}/api/playback/session`, {
						data: {
							session_id: sessionId.value,
						},
					})
				}
			} else {
				// Use DELETE request to remove the session
				await api.delete(`${url}/api/playback/session`, {
					data: {
						session_id: sessionId.value,
					},
				})
			}
		} catch (error) {
			console.error("Failed to end playback session:", error)
			// Don't show toast - this is cleanup
		} finally {
			sessionId.value = null
			isActive.value = false
			isEnding.value = false
		}
	}

	/**
	 * Get active sessions (optional - for debugging/monitoring)
	 */
	const getActiveSessions = async () => {
		try {
			const response = await api.get(`${url}/api/playback/sessions`)
			return response.data
		} catch (error) {
			console.error("Failed to get active sessions:", error)
			return null
		}
	}

	return {
		sessionId,
		isActive,
		startSession,
		sendHeartbeat,
		startHeartbeat,
		stopHeartbeat,
		pauseHeartbeat,
		endSession,
		getActiveSessions,
	}
}
